/**
 * LineTool — Draw a line of tiles between two points using Bresenham's algorithm.
 * Click to set start, drag to preview, release to commit.
 */

export class LineTool {
  constructor(state, mapCanvas, commandHistory) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this.commandHistory = commandHistory;
    this._startX = -1;
    this._startY = -1;
    this._drawing = false;
    this._preview = []; // [{x, y}] for overlay rendering
  }

  onDown(tx, ty) {
    this._startX = tx;
    this._startY = ty;
    this._drawing = true;
    this._preview = [{ x: tx, y: ty }];
    this.state.set('linePreview', this._preview);
  }

  onMove(tx, ty) {
    if (!this._drawing) return;
    this._preview = this._bresenham(this._startX, this._startY, tx, ty);
    this.state.set('linePreview', this._preview);
  }

  onUp(tx, ty) {
    if (!this._drawing) return;
    this._drawing = false;

    const points = this._bresenham(this._startX, this._startY, tx, ty);
    if (points.length === 0) return;

    const layers = this.state.get('layers');
    const activeIdx = this.state.get('activeLayerIndex');
    const layer = layers[activeIdx];
    if (!layer || layer.type !== 'tilelayer' || !layer.data) return;

    const mapW = this.state.get('mapWidth');
    const mapH = this.state.get('mapHeight');
    const tileId = this.state.get('selectedTileId') || 1;
    const flipFlags = this.state.get('tileFlipFlags') || 0;
    const gid = tileId | flipFlags;

    // Save undo state
    const oldData = new Uint32Array(layer.data);

    for (const p of points) {
      if (p.x >= 0 && p.x < mapW && p.y >= 0 && p.y < mapH) {
        layer.data[p.y * mapW + p.x] = gid;
      }
    }

    this.commandHistory.push({
      layerIndex: activeIdx,
      oldData,
      newData: new Uint32Array(layer.data),
    });

    this.state.set('linePreview', null);
    this.state.set('dirty', true);
    this.mapCanvas.invalidateLayer(activeIdx);
  }

  _bresenham(x0, y0, x1, y1) {
    const points = [];
    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;

    while (true) {
      points.push({ x: x0, y: y0 });
      if (x0 === x1 && y0 === y1) break;
      const e2 = 2 * err;
      if (e2 > -dy) { err -= dy; x0 += sx; }
      if (e2 < dx) { err += dx; y0 += sy; }
    }
    return points;
  }
}
